import { useEffect } from "react";
import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Navbar from "./components/Navbar";
import WhatsAppButton from "./components/ui/whatsapp";
import ScrollToTop from "./components/ScrollToTop";
import { MobileNavbar } from "./components/BottomNav";
import Loader from "./components/ui/loader";
import "./index.css";
import { GoogleOAuthProvider } from "@react-oauth/google";
import AuthModal from "./components/auth/AuthModal";
import { AuthProvider } from "./context/AuthContext";
import VerifyOtp from "./components/auth/VerifyOtp";
import { ProductsProvider } from "./context/ProductContext";
import { useModalStore } from "./zustand/modalStore";
import { setUnauthorizedHandler } from "./api/AxiosInstance";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import { ShippingPolicy } from "./pages/ShippingPolicy";
import { CancellationRefundPolicy } from "./pages/CancellationAndRefundPolicy";
import Footer from "./components/Footer";
import AOS from "aos";
import "aos/dist/aos.css";
import AuthCallback from "./pages/CallBack";
import GlobalPopup from "./components/common/globalPopUp";
import ResetPassword from "./pages/ResetPassword";

const Index = lazy(() => import("./pages/Index"));
const About = lazy(() => import("./pages/About"));
const Products = lazy(() => import("./pages/Products"));
const ProductsPage = lazy(() => import("./pages/ProductsPage"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Cart = lazy(() => import("./pages/Cart"));
const BuyNow = lazy(() => import("./pages/BuyNow"));
const OrderPage = lazy(() => import("./pages/Orders"));
const Contact = lazy(() => import("./pages/Contact"));
const ProfilePage = lazy(() => import("./pages/Profile"));
const Wishlist = lazy(() => import("./pages/WishList"));
const OrderConfirm = lazy(() => import("./pages/OrderConfirm"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Home = lazy(() => import("./pages/Home"));
const CouponPage = lazy(() => import("./pages/CouponPage"));

const queryClient = new QueryClient();

const App = () => {
  // const { openLogin } = useModalStore();

  // useEffect(() => {
  //   setUnauthorizedHandler(() => openLogin("login"));
  // }, []);

  useEffect(() => {
    AOS.init({ duration: 1000, once: false });
    AOS.refresh();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <WhatsAppButton />
      <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
        <AuthProvider>
          <ProductsProvider>
            <TooltipProvider>
              <GlobalPopup />
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <ScrollToTop />
                <Navbar />
                <Suspense
                  fallback={
                    <div className="flex items-center justify-center h-screen">
                      <Loader />
                    </div>
                  }
                >
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/products/:category" element={<Products />} />
                    <Route path="/product/:slug" element={<ProductDetail />} />
                    <Route path="/products/" element={<ProductsPage />} />
                    <Route path="/cart" element={<Cart />} />
                    <Route path="/buy-now" element={<BuyNow />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/order-confirm" element={<OrderConfirm />} />
                    <Route path="/orders" element={<OrderPage />} />
                    <Route path="/wishlist" element={<Wishlist />} />
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/verify-otp" element={<VerifyOtp />} />
                    <Route path="/home" element={<Home />} />
                    <Route path="/coupon" element={<CouponPage />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route
                      path="/refund-policy"
                      element={<CancellationRefundPolicy />}
                    />
                    <Route path="/terms" element={<TermsAndConditions />} />
                    <Route
                      path="/shipping-policy"
                      element={<ShippingPolicy />}
                    />
                    <Route path="*" element={<NotFound />} />
                    <Route path="/auth/callback" element={<AuthCallback />} />
                  </Routes>
                </Suspense>
                <Footer />
                <MobileNavbar />

                <AuthModal />
              </BrowserRouter>
            </TooltipProvider>
          </ProductsProvider>
        </AuthProvider>
      </GoogleOAuthProvider>
    </QueryClientProvider>
  );
};

export default App;
